import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, BedDouble, Bath, Maximize, Compass, Building, Calendar, CheckCircle2,
  MessageCircle, Phone, Mail, ArrowLeft, ChevronLeft, ChevronRight, Layers, Send,
} from "lucide-react";
import { EnquiryDialog } from "../components/EnquiryForm";
import api, { imageUrl } from "../lib/api";
import { priceLabel, typeLabel, FURNISHING } from "../lib/constants";
import { useSettings } from "../hooks/useSettings";
import Seo from "../components/Seo";

const PropertyDetail = () => {
  const { slug } = useParams();
  const { data: settings } = useSettings();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [enquiry, setEnquiry] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null); setNotFound(false); setActive(0);
    api.get(`/properties/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-5 py-32 text-center">
      <h1 className="font-heading text-3xl text-stone-900">Property not found</h1>
      <Link to="/properties" className="inline-block mt-6 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold">Back to listings</Link>
    </div>
  );
  if (!p) return <div className="max-w-7xl mx-auto px-5 py-20"><div className="h-[480px] rounded-2xl bg-stone-100 animate-pulse" /></div>;

  const images = (p.images?.length ? p.images : []).map(imageUrl);
  const hasImages = images.length > 0;
  const isResidential = !["plot", "agricultural-land"].includes(p.property_type);
  const wa = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
  const waHref = `https://wa.me/${wa}?text=${encodeURIComponent(`Hi, I'm interested in "${p.title}" (${p.locality}, ${p.city}) listed for ${priceLabel(p)}. Please share more details.`)}`;
  const furnLabel = FURNISHING.find((f) => f.value === p.furnishing)?.label || p.furnishing;

  const specs = [
    isResidential && { icon: BedDouble, label: "Bedrooms", value: `${p.bedrooms} BHK` },
    isResidential && { icon: Bath, label: "Bathrooms", value: p.bathrooms },
    { icon: Maximize, label: "Area", value: `${p.area_value} ${p.area_unit}` },
    isResidential && { icon: Layers, label: "Furnishing", value: furnLabel },
    p.facing && { icon: Compass, label: "Facing", value: p.facing },
    p.floor && { icon: Building, label: "Floor", value: p.floor },
    p.age && { icon: Calendar, label: "Age", value: p.age },
  ].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
      <Seo
        title={`${p.title} in ${p.locality}, ${p.city} — ${priceLabel(p)} | Satyam Real Estate`}
        description={`${typeLabel(p.property_type)} ${p.listing_type === "rent" ? "for rent" : "for sale"} in ${p.locality}, ${p.city}. ${p.area_value} ${p.area_unit}${isResidential ? `, ${p.bedrooms} BHK` : ""}. ${(p.description || "").slice(0, 120)}`}
        image={images[0]}
        jsonLd={{
          "@context": "https://schema.org", "@type": "Product",
          name: p.title, description: p.description, image: images,
          offers: { "@type": "Offer", price: p.price, priceCurrency: "INR" },
        }}
      />

      <Link to="/properties" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-brand mb-5" data-testid="back-link">
        <ArrowLeft size={16} /> Back to listings
      </Link>

      {/* Gallery */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative rounded-2xl overflow-hidden bg-stone-100 aspect-[16/9] group" data-testid="gallery">
        {hasImages ? <img src={images[active]} alt={p.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full grid place-items-center text-stone-400 text-sm">No image available</div>}
        {images.length > 1 && (
          <>
            <button onClick={() => setActive((active - 1 + images.length) % images.length)} data-testid="gallery-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/85 backdrop-blur hover:bg-white transition-colors">
              <ChevronLeft />
            </button>
            <button onClick={() => setActive((active + 1) % images.length)} data-testid="gallery-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/85 backdrop-blur hover:bg-white transition-colors">
              <ChevronRight />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 text-white text-xs px-3 py-1 backdrop-blur">
              {active + 1} / {images.length}
            </div>
          </>
        )}
      </motion.div>
      {images.length > 1 && (
        <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
          {images.map((im, i) => (
            <button key={`${im}-${i}`} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
              className={`shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 transition-colors ${active === i ? "border-brand" : "border-transparent"}`}>
              <img src={im} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-10 mt-8">
        {/* Left */}
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="rounded-full bg-stone-900 text-white text-xs font-semibold px-3 py-1">{p.listing_type === "rent" ? "For Rent" : "For Sale"}</span>
            <span className="rounded-full bg-accent text-brand text-xs font-semibold px-3 py-1">{typeLabel(p.property_type)}</span>
            {p.rera_id && <span className="rounded-full border border-stone-200 text-stone-600 text-xs px-3 py-1">RERA: {p.rera_id}</span>}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">{p.title}</h1>
          <p className="flex items-center gap-1.5 text-stone-500 mt-2"><MapPin size={16} className="text-brand" /> {p.address || `${p.locality}, ${p.city}`}</p>
          <p className="font-heading text-3xl text-brand mt-4" data-testid="detail-price">{priceLabel(p)}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8" data-testid="specs">
            {specs.map((s) => (
              <div key={s.label} className="rounded-xl border border-stone-200 p-4">
                <s.icon className="text-brand mb-2" size={20} />
                <p className="text-xs text-stone-500">{s.label}</p>
                <p className="font-semibold text-stone-800">{s.value}</p>
              </div>
            ))}
          </div>

          {p.description && (
            <div className="mt-10">
              <h2 className="font-heading text-2xl text-stone-900 mb-3">Description</h2>
              <p className="text-stone-600 leading-relaxed whitespace-pre-line">{p.description}</p>
            </div>
          )}

          {p.amenities?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-heading text-2xl text-stone-900 mb-4">Amenities</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="amenities">
                {p.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2 text-sm text-stone-700">
                    <CheckCircle2 size={17} className="text-brand" /> {a}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Map */}
          <div className="mt-10">
            <h2 className="font-heading text-2xl text-stone-900 mb-3">Location</h2>
            <div className="rounded-2xl overflow-hidden border border-stone-200" data-testid="property-map">
              <iframe title="Property location map" width="100%" height="320" loading="lazy" style={{ border: 0 }}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(`${p.locality}, ${p.city}, Rajasthan, India`)}&z=14&output=embed`} />
            </div>
            <p className="text-sm text-stone-500 mt-2 flex items-center gap-1.5"><MapPin size={14} className="text-brand" /> {p.address || `${p.locality}, ${p.city}`}</p>
          </div>
        </div>

        {/* Sticky contact */}
        <aside>
          <div className="sticky top-24 bg-white rounded-2xl border border-stone-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="contact-widget">
            <div className="flex items-center gap-3 pb-5 border-b border-stone-100">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-brand text-white font-heading text-lg">S</span>
              <div>
                <p className="font-semibold text-stone-900">{settings?.firm_name || "Satyam Real Estate"}</p>
                <p className="text-xs text-stone-500">Interested? Contact us directly</p>
              </div>
            </div>
            <div className="space-y-3 mt-5">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="whatsapp-contact-button"
                className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white py-3.5 text-sm font-semibold hover:opacity-90 transition-opacity">
                <MessageCircle size={18} /> WhatsApp Enquiry
              </a>
              <a href={`tel:${settings?.phone}`} data-testid="call-button"
                className="flex items-center justify-center gap-2 rounded-full bg-stone-900 text-white py-3.5 text-sm font-semibold hover:bg-brand transition-colors">
                <Phone size={18} /> Call {settings?.phone}
              </a>
              <a href={`mailto:${settings?.email}?subject=${encodeURIComponent("Enquiry: " + p.title)}`} data-testid="email-button"
                className="flex items-center justify-center gap-2 rounded-full border border-stone-200 text-stone-700 py-3.5 text-sm font-semibold hover:border-brand transition-colors">
                <Mail size={18} /> Email Us
              </a>
              <button onClick={() => setEnquiry(true)} data-testid="open-enquiry-btn"
                className="w-full flex items-center justify-center gap-2 rounded-full border border-brand text-brand py-3.5 text-sm font-semibold hover:bg-accent transition-colors">
                <Send size={18} /> Send Enquiry
              </button>
            </div>
          </div>
        </aside>
      </div>
      <EnquiryDialog open={enquiry} onClose={() => setEnquiry(false)} property={p} />
    </div>
  );
};

export default PropertyDetail;
